import * as Dialog from '@radix-ui/react-dialog';
import { forwardRef } from 'react';

import { SlideOutBody } from './components/SlideOutBody';
import { SlideOutClose } from './components/SlideOutClose';
import { SlideOutContent } from './components/SlideOutContent';
import { SlideOutFooter } from './components/SlideOutFooter';
import { SlideOutHeader } from './components/SlideOutHeader';
import { DEFAULT_SIDE, DEFAULT_WIDTH } from './constants';
import { useSlideOutController } from './hooks/useSlideOutController';
import type { SlideOutProps, SlideOutRef } from './types';

/**
 * Accessible side panel built on Radix Dialog.
 *
 * `ref.open()` and `ref.close()` resolve when their transition finishes.
 * User dismissals (header close, `SlideOut.Close`, overlay click, Escape)
 * can be vetoed with `onBeforeClose`. Imperative `close()` skips that guard.
 * `onStatusChange` reports `isClosed`, `isOpening`, `isOpen`, and `isClosing`.
 */
export const SlideOutComponent = forwardRef<SlideOutRef, SlideOutProps>(
  (
    {
      children,
      panelWidth = DEFAULT_WIDTH,
      side = DEFAULT_SIDE,
      onStatusChange,
      onBeforeClose,
      ...props
    },
    ref,
  ) => {
    const { isOpen, overlayRef, setContentRef, handleOpenChange } =
      useSlideOutController({
        ref,
        panelWidth,
        side,
        onStatusChange,
        onBeforeClose,
      });

    return (
      <Dialog.Root {...props} open={isOpen} onOpenChange={handleOpenChange}>
        <SlideOutContent
          overlayRef={overlayRef}
          contentRef={setContentRef}
          panelWidth={panelWidth}
          side={side}
        >
          {children}
        </SlideOutContent>
      </Dialog.Root>
    );
  },
);

SlideOutComponent.displayName = 'SlideOut';

export const SlideOut = Object.assign(SlideOutComponent, {
  Header: SlideOutHeader,
  Body: SlideOutBody,
  Footer: SlideOutFooter,
  Close: SlideOutClose,
});
